import React, { ReactNode } from 'react';

import { PersonFilterType } from '~/views/audiences/AudiencePage/Tabs/OverviewPage/helpers/PersonFilterHelper';

import { GeoFilter, GeoFilterType } from '../../contexts/FilterContext';
import { FieldType, GeoFilter as GeoFilterGraphQl } from '../../types/api.graphql';
import CircularGeoFilterHelper, { GeoFilterCircleData } from './CircularGeoFilterHelper';
import GeoFilterHelper from './GeoFilterHelper';
import ListGeoFilterHelper, { GeoFilterListData } from './ListGeoFilterHelper';
import PolygonGeoFilterHelper, { GeoFilterPolygonData } from './PolygonGeoFilterHelper';

export const hasRenderableContent = (node: ReactNode): boolean => {
  if ([null, undefined, false, ''].includes(node as string | boolean)) return false;

  // Handle Fragment elements by checking their children
  if (React.isValidElement(node) && node.type === React.Fragment) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const fragmentChildren = React.Children.toArray(node.props.children);
    return fragmentChildren.some((child) => hasRenderableContent(child));
  }

  // Filter out falsy values (null, undefined, false, empty strings)
  const children = React.Children.toArray(node);
  return children.some((child) => {
    // React.Children.toArray filters out null, undefined, and booleans already
    // But we need to check for nested fragments
    if (React.isValidElement(child) && child.type === React.Fragment) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return hasRenderableContent(child.props.children);
    }
    return true;
  });
};

export function createGeoFilterHelper(geoFilter: GeoFilter): GeoFilterHelper {
  switch (geoFilter.type) {
    case GeoFilterType.Circle:
      return new CircularGeoFilterHelper(geoFilter as GeoFilter<GeoFilterCircleData>);
    case GeoFilterType.Polygon:
      return new PolygonGeoFilterHelper(geoFilter as GeoFilter<GeoFilterPolygonData>);
    case GeoFilterType.List:
      return new ListGeoFilterHelper(geoFilter as GeoFilter<GeoFilterListData>);
  }
}

export function createGeoFilterHelperFromGraphQL(geoFilterGraphQL: GeoFilterGraphQl): GeoFilterHelper {
  let instance: GeoFilterHelper;
  if (geoFilterGraphQL.operator.polygon) {
    instance = new PolygonGeoFilterHelper();
  } else if (geoFilterGraphQL.operator.in) {
    instance = new ListGeoFilterHelper();
  } else {
    instance = new CircularGeoFilterHelper();
  }
  instance.geoFilterGraphQL = geoFilterGraphQL;
  return instance;
}

export function createParentGeoFilterHelperFromGraphQL(
  geoFilterGraphQL: GeoFilterGraphQl,
  props: Partial<GeoFilter> = undefined,
): GeoFilter {
  const geoFilter = createGeoFilterHelperFromGraphQL(geoFilterGraphQL).fromGraphQLGeoFilter();
  return { ...geoFilter, isParent: true, ...props };
}

export default GeoFilterHelper;

export const MapFilterToFieldType = {
  [PersonFilterType.Boolean]: FieldType.Boolean,
  [PersonFilterType.ExtendedString]: FieldType.String,
  [PersonFilterType.Number]: FieldType.Number,
  [PersonFilterType.String]: FieldType.String,
  [PersonFilterType.Date]: FieldType.Date,
};
